import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Input,
  HStack,
  VStack,
  IconButton,
  useColorModeValue,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';

interface ColorPickerProps {
  title: string;
  value: string;
  palette?: string[];
  onChange: (color: string) => void;
  disabled?: boolean;
}

const DEFAULT_PALETTE = ['#FCA726', '#F35A66', '#7091DA', '#4ECDC4', '#C758C7'];

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 26 26" fill="currentColor">
    <path d="M5 12l5.02 4.9L21.15 4c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-12.3 14.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.65-.65 1.71-.65 2.36.01z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M15.6,15.5c-0.53,0.53-1.38,0.53-1.91,0L8.05,9.87L2.31,15.6c-0.53,0.53-1.38,0.53-1.91,0 c-0.53-0.53-0.53-1.38,0-1.9l5.65-5.64L0.4,2.4c-0.53-0.53-0.53-1.38,0-1.91c0.53-0.53,1.38-0.53,1.91,0l5.64,5.63l5.74-5.73 c0.53-0.53,1.38-0.53,1.91,0c0.53,0.53,0.53,1.38,0,1.91L9.94,7.94l5.66,5.65C16.12,14.12,16.12,14.97,15.6,15.5z" />
  </svg>
);

export const ColorPicker: React.FC<ColorPickerProps> = ({
  title,
  value,
  palette = DEFAULT_PALETTE,
  onChange,
  disabled = false
}) => {
  const [customColor, setCustomColor] = useState(value);
  const [showPicker, setShowPicker] = useState(false);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.500');

  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  const handleColorChange = (color: string) => {
    setCustomColor(color);
    onChange(color);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    if (newColor.match(/^#[0-9A-Fa-f]{6}$/)) {
      onChange(newColor);
    }
  };

  const isValidColor = customColor.match(/^#[0-9A-Fa-f]{6}$/);

  return (
    <Box mb={6}>
      <Text
        fontSize="sm"
        fontWeight="500"
        color="gray.700"
        mb={3}
      >
        {title}
      </Text>
      <VStack align="stretch" spacing={3}>
        <HStack spacing={2} wrap="wrap">
          {palette.map((color, index) => (
            <Box
              key={index}
              as="button"
              w="32px"
              h="32px"
              borderRadius="md"
              bg={color}
              border="1px solid"
              borderColor={color === value ? 'blue.500' : borderColor}
              _hover={{ borderColor: hoverBorderColor }}
              onClick={() => handleColorChange(color)}
              position="relative"
              _after={color === value ? {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '16px',
                height: '16px',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 26 26'%3E%3Cpath fill='white' d='M5 12l5.02 4.9L21.15 4c.65-.66 1.71-.66 2.36 0 .65.67.65 1.74 0 2.4l-12.3 14.1c-.33.33-.76.5-1.18.5-.43 0-.86-.17-1.18-.5l-6.21-6.1c-.65-.66-.65-1.74 0-2.41.65-.65 1.71-.65 2.36.01z'/%3E%3C/svg%3E")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat'
              } : {}}
            />
          ))}
        </HStack>
        <Box>
          <Text
            fontSize="sm"
            color="gray.700"
            mb={2}
          >
            Custom color
          </Text>
          <HStack spacing={2}>
            <InputGroup size="sm">
              <Input
                value={customColor}
                onChange={handleCustomColorChange}
                maxLength={7}
                placeholder="Custom color"
                size="sm"
                borderRadius="md"
              />
              <InputRightElement>
                {customColor && (
                  isValidColor ? (
                    <Box color="green.500">
                      <CheckIcon />
                    </Box>
                  ) : (
                    <Box color="red.500">
                      <CloseIcon />
                    </Box>
                  )
                )}
              </InputRightElement>
            </InputGroup>
            <IconButton
              aria-label="Pick color"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" focusable="false">
                  <path d="M16.747.82l.433.433a2 2 0 01.054 2.772c-.098.16-.215.311-.354.45l-2.983 2.984a1 1 0 01-.086 1.316l-.115.115a1 1 0 01-1.317.085l-8.08 8.08A2.372 2.372 0 01.946 13.7l8.08-8.08a1 1 0 01.085-1.316l.115-.115a1 1 0 011.316-.086l2.984-2.983c.139-.139.29-.256.45-.353L13.92.82a2 2 0 012.828 0zm-6.89 5.645l-8.073 8.074a1.186 1.186 0 001.573 1.77l.104-.093 8.073-8.074-1.677-1.677z" fill="currentColor" fillRule="nonzero" />
                </svg>
              }
              size="sm"
              variant="outline"
              onClick={() => setShowPicker(!showPicker)}
            />
          </HStack>
          {showPicker && (
            <Box mt={2}>
              <input
                type="color"
                value={customColor}
                onChange={(e) => handleColorChange(e.target.value)}
                style={{
                  width: '100%',
                  height: '40px',
                  padding: 0,
                  border: '1px solid',
                  borderColor: borderColor,
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              />
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
}; 